import React, {
  useCallback, useEffect, useState, ReactElement, Fragment,
} from 'react';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import TableSortLabel from '../../../components/table-sort-label/table-sort-label.component';
import TableCellShort from '../../../components/table-cell-short/table-cell-short.component';
import SearchResultRow from '../search-result-row/search-result-row.component';
import SortService from '../../../services/sort-service';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../../constants/system/sort-types';

const SearchResultTable: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    searchResultData,
    tableSearchResultColumnList,
    tableSearchResultSubGroupColumnList,
    defaultSortState,
    subItemsExpandedMap,
    onRowClick,
  } = props;

  const [sortParams, setSortParams]: [SortParams, Function] = useState(defaultSortState);

  const searchResultRowClick: Function = useCallback((entityId: number, dealId: number): void => {
    if (onRowClick) {
      onRowClick(entityId, dealId);
    }
  }, []);

  const sortTableRows: Function = useCallback((columnName: string, isNumber?: boolean): void => {
    setSortParams({
      field: columnName,
      type: sortParams.field === columnName && sortParams.type === SORT_TYPE_DESC ? SORT_TYPE_ASC : SORT_TYPE_DESC,
      isNumber,
    });
  }, [sortParams]);

  useEffect((): void => {
    if (!sortParams && defaultSortState) {
      setSortParams(defaultSortState);
    }
  }, [defaultSortState]);

  return (
    <Table>
      <TableHead isSubHeader withFixedCellsSize>
        <TableRow>
          {
            tableSearchResultColumnList.map((searchColumnItem: SearchColumnItem): ReactElement => (
              searchColumnItem.isImage ? (
                <TableCellShort key={searchColumnItem.id}>
                  <div>
                    {searchColumnItem.label}
                  </div>
                </TableCellShort>
              ) : (
                <TableCell
                  key={searchColumnItem.id}
                  sortDirection={sortParams && sortParams.field === searchColumnItem.field
                  && searchColumnItem.isSortable ? sortParams.type : false}
                >
                  {
                    searchColumnItem.isSortable ? (
                      <TableSortLabel
                        isActive={sortParams && sortParams.field === searchColumnItem.field}
                        sortType={sortParams && sortParams.type}
                        onClick={(): void => {
                          sortTableRows(searchColumnItem.field, searchColumnItem.isNumber || false);
                        }}
                      >
                        {searchColumnItem.label}
                      </TableSortLabel>
                    ) : (
                      <div>
                        {searchColumnItem.label}
                      </div>
                    )
                  }
                </TableCell>
              )
            ))
          }
        </TableRow>
      </TableHead>
      <TableBody isPlain withZebraRows withFixedCellsSize withBorderedCells>
        {
          searchResultData && sortParams && SortService.sort(searchResultData, sortParams)
            .map((searchResultItem: SearchResultItem): ReactElement => (
              <Fragment key={searchResultItem.id}>
                <SearchResultRow
                  searchResultItem={searchResultItem}
                  tableSearchResultColumnList={tableSearchResultColumnList}
                  onClick={searchResultRowClick}
                />
                {
                  searchResultItem.subItems && !!searchResultItem.subItems.length
                  && !!subItemsExpandedMap && !!subItemsExpandedMap[searchResultItem.id]
                  && searchResultItem.subItems.map((searchResultSubItem: SearchResultItem): ReactElement => (
                    <SearchResultRow
                      isSubItem
                      key={`${searchResultItem.id}-${searchResultSubItem.id}`}
                      searchResultItem={searchResultSubItem}
                      tableSearchResultColumnList={tableSearchResultSubGroupColumnList}
                      onClick={searchResultRowClick}
                    />
                  ))
                }
              </Fragment>
            ))
        }
      </TableBody>
    </Table>
  );
});

export default SearchResultTable;
